import React from 'react'
import { BarChart, BarSeriesType } from '@mui/x-charts'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { formatPercentage } from '../../../packages/utils/numberFormatters'
import { CustomAxisTooltip } from './CustomTooltip'
import Stack from '@mui/system/Stack'

interface HorizontalBarChartProps {
    isLeftAlign?: boolean
    labelValue: number
    series: Array<Pick<BarSeriesType, 'color'> & { data: [number]; label: string }>
    size: 'small' | 'large'
    skipAnimation?: boolean
    valueDecimals?: number
}

type BarSerieWithLabel = Omit<BarSeriesType, 'type'> & { data: [number]; label: string }

function HorizontalBarChart({
    isLeftAlign,
    labelValue,
    series,
    size = 'small',
    skipAnimation = true,
    valueDecimals = 1,
}: HorizontalBarChartProps) {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const valueFormatter = (value: number | null) => formatPercentage(value, { locale, decimals: valueDecimals })

    const mappedSeries: BarSeriesType[] = series.map((s) => ({
        ...s,
        type: 'bar',
        stack: 'A',
        valueFormatter,
        stackOffset: 'expand',
        data: s.data?.map((d) => {
            if (typeof d === 'string') {
                return parseFloat(d)
            }
            return d
        }),
    }))

    const labelSeries = series
        .reduce((acc, item) => {
            const existingObjectIndex = acc.findIndex((i) => i.label === item.label)

            if (existingObjectIndex >= 0 && item.label === 'Ej säkerställt') {
                const existingObject = acc[existingObjectIndex]
                const updatedDataValue = existingObject.data[0] + item.data[0]
                acc[existingObjectIndex] = {
                    ...existingObject,
                    data: [updatedDataValue],
                }
                return acc
            }

            return [...acc, item]
        }, [] as Array<BarSerieWithLabel>)
        .sort((a, b) => {
            if (a.label === 'Ej säkerställt') return 1
            if (b.label === 'Ej säkerställt') return -1
            return 1
        })
    const leftMargin = isLeftAlign ? -45 : -30

    return (
        <>
            <Box
                sx={{
                    height: size === 'small' ? 20 : 50,
                    width: size === 'small' ? undefined : '100%',
                    display: 'flex',
                    // marginLeft: isLeftAlign ? -11 : -7,
                    justifySelf: isLeftAlign ? 'flex-start' : undefined,
                }}
            >
                <BarChart
                    skipAnimation={skipAnimation}
                    layout='horizontal'
                    xAxis={[{ position: 'none', max: 1 }]}
                    slots={{
                        tooltip: size === 'small' ? CustomAxisTooltip : undefined,
                    }}
                    slotProps={{
                        tooltip: {
                            trigger: size === 'small' ? 'axis' : 'item',
                        },
                    }}
                    series={mappedSeries}
                    margin={{
                        top: 0,
                        left: leftMargin,
                        bottom: size === 'small' ? 0 : 20,
                        right: size === 'small' ? 20 : 0,
                    }}
                    sx={{
                        '.MuiChartsAxis-root': {
                            display: 'none',
                        },
                    }}
                    width={size === 'small' ? 160 + leftMargin + 0 : undefined}
                    hideLegend={true}
                />
                {size === 'small' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '70px' }}>
                        {formatPercentage(labelValue, { locale, decimals: 0 })}
                    </Box>
                )}
            </Box>
            {size === 'large' && (
                <Box sx={{ marginTop: 0, marginBottom: 8 }}>
                    <Stack
                        spacing={4}
                        direction={'row'}
                        sx={{
                            display: 'flex',
                        }}
                    >
                        {labelSeries.map((label, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        backgroundColor: label.color,
                                        marginRight: 1,
                                    }}
                                />
                                <Typography variant='body2'>
                                    {label.label}: {formatPercentage(label.data[0], { locale, decimals: 1 })}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}
        </>
    )
}

export default HorizontalBarChart
